import { useQuery, useMutation } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { addEmployeeWorkHours, updateEmployeeSchedule, cancelAppointments, getEmployeeWorkHours, updateEmployeeRecurringBreaks, generateRecurringBreakBlockersForEmployee, getAppointmentsByIds, deleteBlocker } from "../../../firebase/firebase-delegate";
import { generateAllOnesBitString, extractTimesFromBitString, isTimeFree, generateScheduleBitString, isDateBetween, getRecurringBreakInstances, RecurringBreakInstance, parseDateFromMMDDYYYY, formatDateToISO } from "../../../time/timeUtils";
import { WorkHours, BitStringData, Appointment, RecurringBreak, isBlocker, occupiesSchedule } from "../../../types";
import { DAYJS_DAYS_OF_WEEK, TIME_INTERVAL, MAX_WORK_HOURS_UPDATE_RANGE_DAYS } from "../../../constants";
import { useAdminData } from "../../../contexts/AdminDataContext";

const createDefaultWorkHours = (): WorkHours => ({
    monday: generateAllOnesBitString(),
    tuesday: generateAllOnesBitString(),
    wednesday: generateAllOnesBitString(),
    thursday: generateAllOnesBitString(),
    friday: generateAllOnesBitString(),
    saturday: generateAllOnesBitString(),
    sunday: generateAllOnesBitString(),
});

type DayKey = keyof WorkHours;

type SaveSchedulePayload = {
    recurringBreaks: RecurringBreak[];
    recurringBreakIdsToReset?: string[];
};

const parseWorkHours = (workHours: WorkHours): Record<string, BitStringData> =>
    DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
        acc[day] = extractTimesFromBitString(workHours[day]);
        return acc;
    }, {} as Record<string, BitStringData>);

const createEmptyRecurringBreakMap = (): Record<DayKey, RecurringBreak[]> =>
    DAYJS_DAYS_OF_WEEK.reduce((acc, day) => {
        acc[day] = [];
        return acc;
    }, {} as Record<DayKey, RecurringBreak[]>);

const groupRecurringBreaksByDay = (breaks: RecurringBreak[] = []): Record<DayKey, RecurringBreak[]> => {
    const grouped = createEmptyRecurringBreakMap();
    breaks.forEach((definition) => {
        const dayKey = DAYJS_DAYS_OF_WEEK[definition.weekday] as DayKey;
        grouped[dayKey] = [...grouped[dayKey], { ...definition }];
    });
    return grouped;
};

// Flattens the map of recurring breaks into a single array with weekday info.
// So it turns 
// { monday: [break1, break2], tuesday: [break3] } 
// into 
// [ {...break1, weekday:0}, {...break2, weekday:0}, {...break3, weekday:1} ]
// This is useful for saving to the database.
// We use it in the save mutation because we need to send the full list of breaks to save.
function flattenRecurringBreakMap(map: Record<DayKey, RecurringBreak[]>): RecurringBreak[] {
    return DAYJS_DAYS_OF_WEEK.flatMap((day, weekday) => (map[day as DayKey] || []).map((definition) => ({
        ...definition,
        weekday,
    }))
    );
}

// Takes an appointment date string in MMDDYYYY format and returns a Dayjs object at start of day.

export const EditWorkHoursViewModel = () => {
    const [currentWorkHours, setCurrentWorkHours] = useState<WorkHours>(createDefaultWorkHours);

    // parsedSchedule = {<dayOfWeek>:bitstringdata}
    const defaultStartDate = dayjs.tz();
    const defaultEndDate = defaultStartDate.add(3, 'month');

    const [parsedSchedule, setParsedSchedule] = useState<Record<string, BitStringData>>({});
    const [recurringBreaksByDay, setRecurringBreaksByDay] = useState<Record<DayKey, RecurringBreak[]>>(() => createEmptyRecurringBreakMap());
    const [dialogOpen, setDialogOpen] = useState(false);
    const [affectedAppointments, setAffectedAppointments] = useState<Appointment[]>([]);
    const [startDate, setStartDate] = useState<Dayjs>(defaultStartDate);
    const [endDate, setEndDate] = useState<Dayjs>(defaultEndDate);
    const [makeDefaultSchedule, setMakeDefaultSchedule] = useState<boolean>(true);
    const [resultDialogOpen, setResultDialogOpen] = useState(false);

    const { currentEmployeeQuery: employeeQuery, scheduleQuery } = useAdminData();

    const employeeId = employeeQuery.data?.id;

    const appointmentIdsInRange = useMemo(() => {
        if (!employeeQuery.data?.appointments) {
            return [];
        }

        const ids = new Set<string>();
        const start = startDate.startOf('day');
        const end = endDate.startOf('day');
        let cursor = start;

        while (cursor.isBefore(end, 'day') || cursor.isSame(end, 'day')) {
            const key = cursor.format('MMDDYYYY');
            const dailyIds = employeeQuery.data.appointments[key] || [];
            dailyIds.forEach((id) => {
                if (typeof id === 'string' && id.trim().length > 0) {
                    ids.add(id);
                }
            });
            cursor = cursor.add(1, 'day');
        }

        return Array.from(ids).sort();
    }, [employeeQuery.data?.appointments, startDate, endDate]);

    const appointmentsInRangeQuery = useQuery<Appointment[]>({
        queryKey: ['employeeAppointmentsInRange', employeeId, startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'), appointmentIdsInRange],
        queryFn: async () => {
            if (!employeeId) {
                throw new Error('No employee ID found.');
            }
            if (appointmentIdsInRange.length === 0) {
                return [];
            }
            return getAppointmentsByIds(appointmentIdsInRange, undefined, 'EditWorkHours appointmentsInRange');
        },
        enabled: !!employeeId,
        placeholderData: [],
    });

    // When we fetch the appointments, we need to check if there are any conflicts with the current schedule.
    useEffect(() => {
        if (!appointmentsInRangeQuery.isFetched || appointmentsInRangeQuery.isFetching) {
            return;
        }
    }, [appointmentsInRangeQuery.data, appointmentsInRangeQuery.isFetched, appointmentsInRangeQuery.isFetching, appointmentsInRangeQuery.error]);

    // Mutation for updating the schedule and canceling appointments
    const updateScheduleMutation = useMutation({
        mutationFn: async ({ recurringBreaks, recurringBreakIdsToReset }: SaveSchedulePayload) => {
            if (!employeeQuery.data || !startDate || !endDate || !employeeQuery.data?.id) {
                throw new Error('Missing data needed to save.');
            }

            await updateEmployeeRecurringBreaks(employeeQuery.data.id, recurringBreaks);

            if (makeDefaultSchedule) {
                await addEmployeeWorkHours(employeeQuery.data.id, currentWorkHours);
            }
            await updateEmployeeSchedule(employeeQuery.data.id, currentWorkHours, startDate, endDate);

            await generateRecurringBreakBlockersForEmployee({
                employeeId: employeeQuery.data.id,
                windowStart: startDate,
                windowEnd: endDate,
                recurringBreakIdsToReset,
            });
        },
        onSuccess: () => {
            console.log('Schedule updated successfully!');
            setDialogOpen(false);
            fetchWorkHoursMutation.mutate();
            employeeQuery.refetch();
        },
        onError: (error) => {
            console.error('Error updating schedule:', error);
        },
    });

    const cancelAppointmentsMutation = useMutation({
        mutationFn: async (appointments: Appointment[]) => {
            if (!employeeQuery.data) {
                throw new Error('No employee ID found.');
            }
            const blockers = appointments.filter(isBlocker);
            const realAppointments = appointments.filter((appointment) => !isBlocker(appointment));
            await Promise.all([
                realAppointments.length > 0 ? cancelAppointments(realAppointments, scheduleQuery.data ?? undefined) : Promise.resolve(),
                blockers.length > 0 ? Promise.all(blockers.map((blocker) => deleteBlocker(blocker))) : Promise.resolve(),
            ]);
        },
        onError: (error) => {
            console.error('Error canceling appointments:', error);
        },
    });

    const fetchWorkHoursMutation = useMutation({
        mutationFn: async () => {
            const employeeId = employeeQuery.data?.id;
            if (!employeeId) {
                throw new Error('No employee ID found.');
            }

            try {
                const currentWorkHours = await getEmployeeWorkHours(employeeId);
                return { currentWorkHours, parsed: parseWorkHours(currentWorkHours) };
            } catch (error) {
                if (error instanceof Error && error.message.includes('No schedule template found')) {
                    const defaultWorkHours = createDefaultWorkHours();
                    await addEmployeeWorkHours(employeeId, defaultWorkHours);
                    return { currentWorkHours: defaultWorkHours, parsed: parseWorkHours(defaultWorkHours) };
                }

                throw error;
            }
        },
        onSuccess: (data) => {
            setCurrentWorkHours(data.currentWorkHours);
            setParsedSchedule(data.parsed);
        },
        onError: (error) => {
            console.error("error fetching schedule", error);
        },
    });

    useEffect(() => {
        if (employeeQuery.data?.id) {
            fetchWorkHoursMutation.mutate();
        }
    }, [employeeQuery.data?.id]);

    // Keep the local break map in sync with the currently selected employee
    useEffect(() => {
        if (!employeeQuery.data?.id) {
            return;
        }
        const recurringBreakDefinitions = employeeQuery.data.recurringBreaks || [];
        setRecurringBreaksByDay(groupRecurringBreaksByDay(recurringBreakDefinitions));
    }, [employeeQuery.data?.id, employeeQuery.data?.recurringBreaks]);

    const onSaveMutation = useMutation({
        mutationFn: async () => {
            if (!employeeQuery.data || !startDate || !endDate) {
                throw new Error("Missing info to save.");
            }
            if (affectedAppointments.length > 0) {
                // Cancel affected appointments if any exist
                await cancelAppointmentsMutation.mutateAsync(affectedAppointments);
            }

            // 1. Flatten the recurring breaks map into a single array with weekday info
            // 2. Prepare the payload for saving, including setting indefinite and repeatUntilDate
            // 3. Call the update schedule mutation; the mutation figures out which blockers need to be added/removed
            const flattenedBreaks = flattenAllRecurringBreaks(recurringBreaksByDay);
            const repeatUntilDate = makeDefaultSchedule ? undefined : formatDateToISO(endDate);
            const serializedBreaks = flattenedBreaks.map((definition) => ({
                ...definition,
                indefinite: makeDefaultSchedule,
                ...(makeDefaultSchedule ? {} : { repeatUntilDate }),
            }));
            const payload: SaveSchedulePayload = {
                recurringBreaks: serializedBreaks,
            };

            // Proceed with saving work hours and schedule update
            await updateScheduleMutation.mutateAsync(payload);
        },
        onSuccess: () => {
            setResultDialogOpen(true);

        },
        onError: () => {
            setResultDialogOpen(true);
        }
    });

    const flattenAllRecurringBreaks = (mapValue: Record<DayKey, RecurringBreak[]>) =>
        flattenRecurringBreakMap(mapValue);

    const computeAffectedAppointments = (
        workHoursSnapshot: WorkHours,
        breakMapSnapshot: Record<DayKey, RecurringBreak[]>,
        rangeStart: Dayjs,
        rangeEnd: Dayjs,
        appointments: Appointment[] = []
    ): Appointment[] => {
        if (!employeeQuery.data || !appointments || appointments.length === 0) {
            return [];
        }

        // Filter appointments to those that occupy the schedule and are not recurring break blockers
        const relevantAppointments = appointments.filter((appointment) => {
            // Skip appointments that do not occupy the schedule
            // Like we skip completed or canceled appointments
            if (!occupiesSchedule(appointment)) {
                return false;
            }
            // If its not a blocker here that means it's an appointment
            if (!isBlocker(appointment)) {
                return true;
            }
            // For blockers, only consider those that are recurring break blockers
            return !appointment.recurringBreakId;
        });

        const breakInstances = getRecurringBreakInstances(
            flattenAllRecurringBreaks(breakMapSnapshot),
            rangeStart,
            rangeEnd
        );
        const breaksByDate = breakInstances.reduce<Map<string, RecurringBreakInstance[]>>((acc, instance) => {
            const key = instance.start.format('MMDDYYYY');
            if (!acc.has(key)) {
                acc.set(key, []);
            }
            acc.get(key)!.push(instance);
            return acc;
        }, new Map());

        const conflicts: Appointment[] = [];
        relevantAppointments.forEach((appointment) => {
            const appointmentDate = parseDateFromMMDDYYYY(appointment.appointmentDate).startOf('day');
            if (!isDateBetween(appointmentDate, rangeStart, rangeEnd, true)) {
                return;
            }

            const dayKey = DAYJS_DAYS_OF_WEEK[appointmentDate.day()] as DayKey;
            const bitString = workHoursSnapshot[dayKey];

            const hour = parseInt(appointment.appointmentTime.substring(0, 2), 10);
            const minute = parseInt(appointment.appointmentTime.substring(2, 4), 10);
            const startTime = appointmentDate.clone().hour(hour).minute(minute).second(0);
            const endTime = startTime.add(appointment.appointmentDuration, 'minute');

            let conflict = false;
            let cursor = startTime.clone();
            while (cursor.isBefore(endTime)) {
                const bitIndex = cursor.hour() * 60 + cursor.minute();
                if (!isTimeFree(bitString, bitIndex)) {
                    conflict = true;
                    break;
                }
                cursor = cursor.add(TIME_INTERVAL, 'minute');
            }

            if (!conflict) {
                const dateKey = appointmentDate.format('MMDDYYYY');
                const dayBreaks = breaksByDate.get(dateKey) || [];
                conflict = dayBreaks.some(
                    (instance) =>
                        instance.start.isBefore(endTime) && instance.end.isAfter(startTime)
                );
            }

            if (conflict) {
                conflicts.push(appointment);
            }
        });

        return conflicts;
    };

    const refreshAffectedAppointments = (
        workHoursSnapshot: WorkHours = currentWorkHours,
        breakMapSnapshot: Record<DayKey, RecurringBreak[]> = recurringBreaksByDay,
        rangeStart: Dayjs = startDate,
        rangeEnd: Dayjs = endDate,
        appointments: Appointment[] = appointmentsInRangeQuery.data ?? []
    ) => {
        const conflicts = computeAffectedAppointments(
            workHoursSnapshot,
            breakMapSnapshot,
            rangeStart,
            rangeEnd,
            appointments
        );
        setAffectedAppointments(conflicts);
    };

    useEffect(() => {
        if (appointmentIdsInRange.length === 0) {
            setAffectedAppointments((current) => (current.length === 0 ? current : []));
            return;
        }

        if (!appointmentsInRangeQuery.isFetched || appointmentsInRangeQuery.isFetching) {
            return;
        }

        const appointments = appointmentsInRangeQuery.data ?? [];
        refreshAffectedAppointments(currentWorkHours, recurringBreaksByDay, startDate, endDate, appointments);
    }, [
        appointmentIdsInRange,
        appointmentsInRangeQuery.data,
        appointmentsInRangeQuery.isFetched,
        appointmentsInRangeQuery.isFetching,
        currentWorkHours,
        recurringBreaksByDay,
        startDate,
        endDate,
    ]);

    // When we change the schedule it will regrab the affected appointments but not when we change date
    const handleScheduleChange = (day: DayKey, updatedSchedule: BitStringData) => {
        const updatedCurrentWorkHours = {
            ...currentWorkHours,
            [day]: generateScheduleBitString(updatedSchedule),
        };

        setCurrentWorkHours(updatedCurrentWorkHours);
        setParsedSchedule((previous) => ({
            ...previous,
            [day]: updatedSchedule,
        }));
        refreshAffectedAppointments(updatedCurrentWorkHours, recurringBreaksByDay, startDate, endDate, appointmentsInRangeQuery.data ?? []);
    };

    const handleRecurringBreaksChange = (day: DayKey, breaks: RecurringBreak[]) => {
        const updatedBreakMap = {
            ...recurringBreaksByDay,
            [day]: breaks,
        };
        setRecurringBreaksByDay(updatedBreakMap);
        refreshAffectedAppointments(currentWorkHours, updatedBreakMap, startDate, endDate, appointmentsInRangeQuery.data ?? []);
    };


    const handleDateRangeChange = (newStartDate: Dayjs, newEndDate: Dayjs) => {
        if (!newStartDate || !newEndDate || newStartDate.isAfter(newEndDate)) {
            console.error("Invalid date range");
            return;
        }
        if (newEndDate.diff(newStartDate, 'day') > MAX_WORK_HOURS_UPDATE_RANGE_DAYS) {
            console.error("Date range exceeds supported limit");
            return;
        }

        refreshAffectedAppointments(currentWorkHours, recurringBreaksByDay, newStartDate, newEndDate, appointmentsInRangeQuery.data ?? []);
    };


    // Close the result dialog and refetch data
    const onCloseResultDialog = () => {
        setResultDialogOpen(false);
        window.location.reload();
    };

    const onStartDateChange = (newDate: Dayjs) => {
        if (makeDefaultSchedule) {
            const autoEndDate = newDate.add(3, 'month');
            handleDateRangeChange(newDate, autoEndDate);
            setEndDate(autoEndDate);
        } else {
            handleDateRangeChange(newDate, endDate);
        }
        setStartDate(newDate);
    };

    const onEndDateChange = (newDate: Dayjs) => {
        handleDateRangeChange(startDate, newDate);
        setEndDate(newDate)
    };

    const onMakeDefaultScheduleChange = (checked: boolean) => {
        setMakeDefaultSchedule(checked);
        if (checked) {
            const autoEndDate = startDate.add(3, 'month');
            handleDateRangeChange(startDate, autoEndDate);
            setEndDate(autoEndDate);
        }
    };

    // Consolidated loading and error states for the page
    const isLoading =
        employeeQuery.isLoading ||
        employeeQuery.isFetching ||
        fetchWorkHoursMutation.isPending ||
        (appointmentsInRangeQuery.isLoading && appointmentIdsInRange.length > 0);

    const isSetUpError = employeeQuery.isError ||
        fetchWorkHoursMutation.isError ||
        appointmentsInRangeQuery.isError;

    // We do not need to keep track of other errors of this bc the 
    // success or dialog watches these when we save. 

    const errorMessage =
        employeeQuery.error?.message ||
        cancelAppointmentsMutation.error?.message ||
        fetchWorkHoursMutation.error?.message ||
        updateScheduleMutation.error?.message ||
        appointmentsInRangeQuery.error?.message;

    const isSaving = onSaveMutation.isPending;

    return {
        parsedSchedule,
        dialogOpen,
        setDialogOpen,
        affectedAppointments,
        startDate,
        endDate,
        makeDefaultSchedule,
        setMakeDefaultSchedule,
        resultDialogOpen,
        onCloseResultDialog,
        fetchWorkHoursMutation,
        onSaveMutation,
        handleScheduleChange,
        recurringBreaksByDay,
        handleRecurringBreaksChange,
        onStartDateChange,
        onEndDateChange,
        onMakeDefaultScheduleChange,
        isLoading,
        isSetUpError,
        errorMessage,
        isSaving,
    };


}
